import { Spinner } from "@phosphor-icons/react";
import { useEffect, useRef, useState } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import { FileText } from "lucide-react";

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

type PDFRendererProps = {
	signedUrl: string;
	onPreviewClick?: (url: string, isPdf: boolean) => void;
};

const PDFRenderer = ({ signedUrl, onPreviewClick }: PDFRendererProps) => {
	const [numPages, setNumPages] = useState<number | null>(null);
	const containerRef = useRef<HTMLDivElement>(null);
	const [containerSize, setContainerSize] = useState<{ width: number; height: number } | undefined>(undefined);
	const [pageAspectRatio, setPageAspectRatio] = useState<number | null>(null);

	useEffect(() => {
		const el = containerRef.current;
		if (!el) return;

		const observer = new ResizeObserver(([entry]) => {
			const { inlineSize: width, blockSize: height } = entry.contentBoxSize[0];
			if (width > 0 && height > 0) setContainerSize({ width, height });
		});

		observer.observe(el);
		return () => observer.disconnect();
	}, []);

	const pageWidth = (() => {
		if (!containerSize || !pageAspectRatio) return containerSize?.width;
		const { width, height } = containerSize;
		const heightAtFullWidth = width / pageAspectRatio;
		return heightAtFullWidth <= height ? width : height * pageAspectRatio;
	})();

	return (
		<div
			ref={containerRef}
			onClick={() => onPreviewClick?.(signedUrl, true)}
			className="h-full w-full flex items-center justify-center overflow-hidden">
			<Document
				file={signedUrl}
				onLoadSuccess={({ numPages }) => setNumPages(numPages)}
				onLoadError={(error) => console.error("Failed to load PDF:", error)}
				loading={
					<div className="flex h-full w-full items-center justify-center">
						<Spinner className="text-indigo-500" size={24} />
					</div>
				}
				error={
					<div role="alert" className="flex h-full w-full items-center justify-center">
						<div className="flex h-12 w-12 items-center justify-center rounded-full bg-indigo-500/20">
							<FileText size={20} className="text-indigo-500" strokeWidth={2.5} aria-hidden="true" />
						</div>
						<span className="sr-only">Failed to load PDF document.</span>
					</div>
				}>
				{numPages && containerSize && (
					<Page
						pageNumber={1}
						width={pageWidth}
						onLoadSuccess={(page) => {
							const vp = page.getViewport({ scale: 1 });
							setPageAspectRatio(vp.width / vp.height);
						}}
						renderAnnotationLayer={false}
						renderTextLayer={false}
					/>
				)}
			</Document>
		</div>
	);
};

export default PDFRenderer;
